// src/pages/EventDisplayPage.js
// Admin UI to configure: event-level registration message, plus per-ticket-type
// display toggles (member/non-member/generic price), generic price value, and
// per-ticket description.
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function EventDisplayPage() {
  const { isManagerOrAbove } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(() => {
    const fromUrl = searchParams.get('event');
    return fromUrl ? parseInt(fromUrl, 10) : null;
  });
  const [data, setData] = useState(null); // { event, ticketTypes }
  const [loading, setLoading] = useState(false);

  // Load events
  useEffect(() => {
    api.get('/ticket-display/events-list')
      .then(res => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Could not load events.'));
  }, []);

  // Load data when event changes
  useEffect(() => {
    if (!selectedEventId) { setData(null); return; }
    setSearchParams({ event: String(selectedEventId) });
    setLoading(true);
    api.get(`/ticket-display/event/${selectedEventId}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Could not load event.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const refresh = () => {
    if (!selectedEventId) return;
    api.get(`/ticket-display/event/${selectedEventId}`).then(res => setData(res.data));
  };

  const upcoming = events.filter(e => !e.event_date || new Date(e.event_date) >= new Date(new Date().toDateString()));
  const past     = events.filter(e =>  e.event_date && new Date(e.event_date) <  new Date(new Date().toDateString()));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Event Display</h1>
          <p className="page-subtitle">Customize what appears on the public registration page.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Event</label>
          <select
            value={selectedEventId || ''}
            onChange={e => setSelectedEventId(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={selectStyle}
          >
            <option value="">— Select an event —</option>
            {upcoming.length > 0 && (
              <optgroup label="Upcoming">
                {upcoming.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} {ev.event_date ? `— ${formatDate(ev.event_date)}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {past.length > 0 && (
              <optgroup label="Past">
                {past.slice(0, 50).map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} — {formatDate(ev.event_date)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      {!selectedEventId ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48, color: 'var(--gray-500)' }}>
            Select an event above to customize its display.
          </div>
        </div>
      ) : loading || !data ? (
        <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 32 }}>Loading…</div></div>
      ) : (
        <>
          <EventMessageCard event={data.event} canEdit={isManagerOrAbove()} onSaved={refresh} />
          <TicketTypesCard ticketTypes={data.ticketTypes} canEdit={isManagerOrAbove()} onSaved={refresh} />
        </>
      )}
    </div>
  );
}

// ─── EventMessageCard ──────────────────────────────────────────────────────
function EventMessageCard({ event, canEdit, onSaved }) {
  const [msg, setMsg] = useState(event.registration_message || '');
  const [saving, setSaving] = useState(false);
  const dirty = msg !== (event.registration_message || '');

  useEffect(() => { setMsg(event.registration_message || ''); }, [event.id, event.registration_message]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/ticket-display/event/${event.id}`, { registrationMessage: msg });
      toast.success('Event message saved.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">Event Message</span>
      </div>
      <div className="card-body">
        <p style={{ marginTop: 0, marginBottom: 10, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
          Shown above the Tickets section on the public registration page. Optional — leave blank to hide.
        </p>
        <textarea
          value={msg}
          onChange={e => setMsg(e.target.value)}
          disabled={!canEdit}
          rows={3}
          style={textareaStyle}
          placeholder='e.g. "All proceeds support the NJJBA scholarship fund."'
        />
        {canEdit && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save Event Message'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TicketTypesCard ───────────────────────────────────────────────────────
function TicketTypesCard({ ticketTypes, canEdit, onSaved }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Ticket Types — Display Settings</span>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        {ticketTypes.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>
            This event has no ticket types yet. Add them from the event detail page.
          </div>
        ) : (
          ticketTypes.map((tt, i) => (
            <TicketTypeBlock
              key={tt.id}
              tt={tt}
              canEdit={canEdit}
              onSaved={onSaved}
              isLast={i === ticketTypes.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── TicketTypeBlock ───────────────────────────────────────────────────────
function TicketTypeBlock({ tt, canEdit, onSaved, isLast }) {
  const [form, setForm] = useState({
    showMemberPrice:    tt.show_member_price,
    showNonMemberPrice: tt.show_non_member_price,
    showGenericPrice:   tt.show_generic_price,
    genericPrice:       tt.generic_price != null ? String(tt.generic_price) : '',
    description:        tt.description || '',
    category:           tt.category || 'ticket',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      showMemberPrice:    tt.show_member_price,
      showNonMemberPrice: tt.show_non_member_price,
      showGenericPrice:   tt.show_generic_price,
      genericPrice:       tt.generic_price != null ? String(tt.generic_price) : '',
      description:        tt.description || '',
      category:           tt.category || 'ticket',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tt.id]);

  const dirty =
    form.showMemberPrice    !== tt.show_member_price    ||
    form.showNonMemberPrice !== tt.show_non_member_price ||
    form.showGenericPrice   !== tt.show_generic_price   ||
    String(form.genericPrice) !== String(tt.generic_price != null ? tt.generic_price : '') ||
    form.description !== (tt.description || '') ||
    form.category !== (tt.category || 'ticket');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/ticket-display/ticket-type/${tt.id}`, {
        showMemberPrice:    form.showMemberPrice,
        showNonMemberPrice: form.showNonMemberPrice,
        showGenericPrice:   form.showGenericPrice,
        genericPrice:       form.genericPrice === '' ? null : parseFloat(form.genericPrice),
        description:        form.description,
        category:           form.category,
      });
      toast.success(`Saved "${tt.name}"`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      padding: 20,
      borderBottom: isLast ? 'none' : '1px solid var(--gray-200)',
      background: dirty ? '#fffbeb' : 'transparent',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, display: 'inline-block' }}>{tt.name}</h3>
          {form.category === 'sponsorship' && (
            <span style={{
              marginLeft: 8, fontSize: '0.7rem', padding: '2px 8px',
              background: '#fef3c7', color: '#92400e',
              borderRadius: 10, fontWeight: 700, textTransform: 'uppercase',
              verticalAlign: 'middle',
            }}>Sponsorship</span>
          )}
        </div>
        {!tt.is_active && (
          <span style={{ fontSize: '0.7rem', padding: '2px 8px', background: 'var(--gray-200)', borderRadius: 10, color: 'var(--gray-600)', textTransform: 'uppercase', fontWeight: 700 }}>
            Inactive
          </span>
        )}
      </div>

      <div style={{ padding: 12, background: form.category === 'sponsorship' ? '#fef3c7' : 'var(--gray-50)', borderRadius: 6, marginBottom: 14, border: form.category === 'sponsorship' ? '1px solid #fde68a' : 'none' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: '0.9rem', cursor: canEdit ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={form.category === 'sponsorship'}
            onChange={e => set('category', e.target.checked ? 'sponsorship' : 'ticket')}
            disabled={!canEdit}
          />
          Show this as a sponsorship option (not a regular ticket)
        </label>
        <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 6, marginLeft: 24 }}>
          When checked, this appears in a separate "Sponsorship Opportunities" section on the public event page.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
        <PriceToggle
          label="Member Price"
          value={tt.member_price}
          checked={form.showMemberPrice}
          onChange={v => set('showMemberPrice', v)}
          disabled={!canEdit}
        />
        <PriceToggle
          label="Non-Member Price"
          value={tt.non_member_price}
          checked={form.showNonMemberPrice}
          onChange={v => set('showNonMemberPrice', v)}
          disabled={!canEdit}
        />
      </div>

      <div style={{ padding: 12, background: 'var(--gray-50)', borderRadius: 6, marginBottom: 14 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: '0.9rem', cursor: canEdit ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={form.showGenericPrice}
            onChange={e => set('showGenericPrice', e.target.checked)}
            disabled={!canEdit}
          />
          Show "Ticket Price" (a single price not tied to membership)
        </label>
        {form.showGenericPrice && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 18, color: 'var(--gray-600)' }}>$</span>
            <input
              type="number" step="0.01" min="0"
              value={form.genericPrice}
              onChange={e => set('genericPrice', e.target.value)}
              disabled={!canEdit}
              placeholder="0.00"
              style={{ ...inputStyle, maxWidth: 160 }}
            />
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              When this is on, the registrant pays this price (instead of the non-member price).
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>
          Description / Note
        </label>
        <textarea
          value={form.description}
          onChange={e => set('description', e.target.value)}
          disabled={!canEdit}
          rows={2}
          style={textareaStyle}
          placeholder='e.g. "Includes lunch and program materials."'
        />
        <div style={hintStyle}>Shown under this ticket name on the registration page. Optional.</div>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}

function PriceToggle({ label, value, checked, onChange, disabled }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: 12, background: 'var(--gray-50)', borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Show {label}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>
          Current value: ${value != null ? parseFloat(value).toFixed(2) : '—'}
        </div>
      </div>
    </label>
  );
}

// ─── Helpers / Styles ──────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const selectStyle = {
  width: '100%', maxWidth: 500, padding: '10px 12px',
  fontSize: '0.95rem', border: '1px solid var(--gray-300)',
  borderRadius: 6, background: '#fff',
};
const inputStyle = {
  padding: '8px 12px', fontSize: '0.95rem',
  border: '1px solid var(--gray-300)', borderRadius: 6,
  background: '#fff', boxSizing: 'border-box',
};
const textareaStyle = {
  width: '100%', padding: '10px 12px', fontSize: '0.95rem',
  border: '1px solid var(--gray-300)', borderRadius: 6,
  background: '#fff', boxSizing: 'border-box',
  fontFamily: 'inherit', resize: 'vertical',
};
const hintStyle = { fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 3 };
